import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Heart, Loader2, DollarSign, Gift, CreditCard, Building2, Copy, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Datos bancarios para SPEI
const DATOS_BANCARIOS = {
  banco: "BBVA",
  titular: "Virginia Ivette Becerra Vadillo",
  clabe: "012320004892269587",
  cuenta: "0489226958"
};

// Comisión de Stripe
const STRIPE_PORCENTAJE = 0.036;
const STRIPE_FIJO = 3;

const DonacionesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tipoAportacion, setTipoAportacion] = useState("monetaria"); // "monetaria" o "especie"
  const [metodoPago, setMetodoPago] = useState("spei"); // "tarjeta" o "spei"
  const [ordenCreada, setOrdenCreada] = useState(null);
  const [copiado, setCopiado] = useState("");
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    monto: "",
    mensaje: ""
  });

  const montosPreseleccionados = [100, 200, 500];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const seleccionarMonto = (monto) => {
    setFormData(prev => ({ ...prev, monto: monto.toString() }));
  };

  const copiarAlPortapapeles = (texto, campo) => {
    navigator.clipboard.writeText(texto);
    setCopiado(campo);
    toast.success("Copiado al portapapeles");
    setTimeout(() => setCopiado(""), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.email) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    // Si es aportación en especie, solo guardar en DB sin Stripe
    if (tipoAportacion === "especie") {
      if (!formData.mensaje.trim()) {
        toast.error("Por favor describe tu aportación en especie");
        return;
      }
      setLoading(true);
      try {
        await axios.post(`${API}/aportaciones/especie`, {
          nombre: formData.nombre,
          email: formData.email,
          descripcion: formData.mensaje
        });
        toast.success("¡Gracias por tu aportación! Nos pondremos en contacto contigo.");
        navigate("/");
      } catch (error) {
        console.error("Error registrando aportación:", error);
        toast.error("Error al registrar la aportación. Inténtalo de nuevo.");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Aportación monetaria
    if (!formData.monto) {
      toast.error("Por favor ingresa un monto");
      return;
    }

    const montoNum = parseFloat(formData.monto);
    if (isNaN(montoNum) || montoNum <= 0) {
      toast.error("Por favor ingresa un monto válido");
      return;
    }

    setLoading(true);

    try {
      // Si es SPEI, crear orden SPEI
      if (metodoPago === "spei") {
        const response = await axios.post(`${API}/donaciones/crear-spei`, {
          ...formData,
          monto: montoNum
        });
        
        setOrdenCreada({
          referencia: response.data.referencia,
          monto: montoNum
        });
        toast.success("Orden creada. Realiza tu transferencia SPEI.");
        setLoading(false);
        return;
      }

      // Pago con tarjeta (Stripe)
      const response = await axios.post(`${API}/donaciones/crear`, {
        ...formData,
        monto: montoNum
      });
      
      // Redirigir a Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creando aportación:", error);
      toast.error("Error al procesar la aportación. Inténtalo de nuevo.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-warm-bone py-12">
      <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-4xl">
        <Button
          onClick={() => navigate("/")}
          variant="ghost"
          className="mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver al Inicio
        </Button>

        <div className="glass-card rounded-3xl p-8 md:p-12">
          <div className="flex items-center gap-4 mb-6">
            <Heart className="h-12 w-12 text-zuno-wine" fill="#722F37" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-zuno-wine">
                Aporta para el Evento
              </h1>
              <p className="text-lg text-slate-600 mt-2">
                Toda aportación será dirigida a cubrir los boletos del evento de nuestros Maestros.
              </p>
            </div>
          </div>

          {/* Selector de tipo de aportación */}
          <div className="mb-8">
            <Label className="text-lg font-semibold mb-4 block">¿Cómo deseas aportar?</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Button
                type="button"
                onClick={() => setTipoAportacion("monetaria")}
                variant={tipoAportacion === "monetaria" ? "default" : "outline"}
                className={`h-auto py-4 sm:py-6 flex flex-col items-center gap-2 sm:gap-3 ${
                  tipoAportacion === "monetaria" ? "bg-zuno-wine text-white" : ""
                }`}
                data-testid="tipo-monetaria"
              >
                <DollarSign size={28} className="sm:w-8 sm:h-8" />
                <span className="text-base sm:text-lg font-semibold">Aportación Monetaria</span>
              </Button>
              <Button
                type="button"
                onClick={() => setTipoAportacion("especie")}
                variant={tipoAportacion === "especie" ? "default" : "outline"}
                className={`h-auto py-4 sm:py-6 flex flex-col items-center gap-2 sm:gap-3 ${
                  tipoAportacion === "especie" ? "bg-zuno-wine text-white" : ""
                }`}
                data-testid="tipo-especie"
              >
                <Gift size={28} className="sm:w-8 sm:h-8" />
                <span className="text-base sm:text-lg font-semibold">Aportación en Especie</span>
              </Button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 mt-8">
            <div>
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Tu nombre"
                required
                data-testid="input-nombre"
              />
            </div>

            <div>
              <Label htmlFor="email">Correo Electrónico *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="correo@ejemplo.com"
                required
                data-testid="input-email"
              />
            </div>

            {tipoAportacion === "monetaria" ? (
              <>
                <div>
                  <Label>Monto Sugerido</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    {montosPreseleccionados.map((monto) => (
                      <Button
                        key={monto}
                        type="button"
                        onClick={() => seleccionarMonto(monto)}
                        variant={formData.monto === monto.toString() ? "default" : "outline"}
                        className={formData.monto === monto.toString() ? "bg-zuno-wine text-white" : ""}
                        data-testid={`preset-${monto}`}
                      >
                        ${monto} MXN
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="monto">Monto Personalizado *</Label>
                  <Input
                    id="monto"
                    name="monto"
                    type="number"
                    min="1"
                    step="0.01"
                    value={formData.monto}
                    onChange={handleChange}
                    placeholder="0.00"
                    required
                    data-testid="input-monto"
                  />
                </div>

                <div>
                  <Label htmlFor="mensaje">Mensaje (Opcional)</Label>
                  <Textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    placeholder="Deja un mensaje de apoyo..."
                    rows={4}
                    data-testid="input-mensaje"
                  />
                </div>

                <Card className="p-6 bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span>Monto de aportación:</span>
                      <span className="font-semibold">${formData.monto || "0"} MXN</span>
                    </div>
                    {metodoPago === "tarjeta" && parseFloat(formData.monto) > 0 && (
                      <div className="flex justify-between items-center text-slate-600">
                        <span>Comisión procesamiento (3.6% + $3):</span>
                        <span>${Math.round(parseFloat(formData.monto || 0) * STRIPE_PORCENTAJE + STRIPE_FIJO)} MXN</span>
                      </div>
                    )}
                    <div className="border-t-2 border-zuno-wine pt-3 flex justify-between items-center">
                      <span className="text-xl font-bold text-zuno-wine">Aportación Total:</span>
                      <span className="text-2xl font-bold text-zuno-wine">
                        ${metodoPago === "tarjeta" && parseFloat(formData.monto) > 0
                          ? Math.round(parseFloat(formData.monto || 0) * (1 + STRIPE_PORCENTAJE) + STRIPE_FIJO)
                          : formData.monto || "0"
                        } MXN
                      </span>
                    </div>
                  </div>
                </Card>

                {/* Selector de método de pago - Solo SPEI */}
                {!ordenCreada && (
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold">Método de Pago</Label>
                    <div className="flex justify-center">
                      <Button
                        type="button"
                        onClick={() => setMetodoPago("spei")}
                        variant="default"
                        className="h-auto py-4 flex flex-col items-center gap-2 bg-zuno-wine text-white w-full max-w-sm"
                      >
                        <Building2 size={28} />
                        <span className="font-semibold">Transferencia SPEI</span>
                        <span className="text-xs opacity-80">Sin comisión adicional</span>
                      </Button>
                    </div>
                  </div>
                )}

                {/* Datos bancarios si se creó orden SPEI */}
                {ordenCreada && (
                  <Card className="p-6 bg-blue-50 border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle className="text-green-600" size={28} />
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">¡Orden Creada!</h3>
                        <p className="text-sm text-slate-600">Referencia: <strong>{ordenCreada.referencia}</strong></p>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Banco:</span>
                        <span className="font-semibold">{DATOS_BANCARIOS.banco}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Titular:</span>
                        <span className="font-semibold">{DATOS_BANCARIOS.titular}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">No. Cuenta:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">{DATOS_BANCARIOS.cuenta}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => copiarAlPortapapeles(DATOS_BANCARIOS.cuenta, "cuenta")}
                          >
                            {copiado === "cuenta" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">CLABE:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">{DATOS_BANCARIOS.clabe}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => copiarAlPortapapeles(DATOS_BANCARIOS.clabe, "clabe")}
                          >
                            {copiado === "clabe" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                          </Button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center border-t pt-3">
                        <span className="text-slate-600">Monto:</span>
                        <span className="text-2xl font-bold text-zuno-wine">${ordenCreada.monto} MXN</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Concepto:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-zuno-wine">{ordenCreada.referencia}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => copiarAlPortapapeles(ordenCreada.referencia, "ref")}
                          >
                            {copiado === "ref" ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                          </Button>
                        </div>
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={() => navigate("/")}
                      className="w-full mt-4 btn-primary"
                    >
                      Volver al Inicio
                    </Button>
                  </Card>
                )}
              </>
            ) : (
              <div>
                <Label htmlFor="mensaje">
                  Describe tu aportación en especie *
                  <span className="block text-sm font-normal text-slate-500 mt-1">
                    Por favor detalla qué tipo de aportación deseas hacer (ej: servicios, productos, apoyo logístico, etc.)
                  </span>
                </Label>
                <Textarea
                  id="mensaje"
                  name="mensaje"
                  value={formData.mensaje}
                  onChange={handleChange}
                  placeholder="Ej: Puedo aportar con bebidas, servicio de fotografía, apoyo en la organización..."
                  rows={6}
                  required={tipoAportacion === "especie"}
                  data-testid="input-mensaje-especie"
                />
              </div>
            )}

            {!ordenCreada && (
              <Button
                type="submit"
                className="w-full btn-primary text-xl py-6"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Heart className="mr-2" fill="white" />
                    {tipoAportacion === "monetaria" 
                      ? (metodoPago === "spei" ? "Generar Orden SPEI" : "Realizar Aportación")
                      : "Registrar Aportación"
                    }
                  </>
                )}
              </Button>
            )}
          </form>

          {!ordenCreada && (
            <p className="text-sm text-slate-500 mt-6 text-center">
              {tipoAportacion === "monetaria" 
                ? "Se generará una orden con los datos para tu transferencia SPEI"
                : "Nos pondremos en contacto contigo para coordinar los detalles de tu aportación"
              }
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonacionesPage;
